import { useId, type InputHTMLAttributes, type ReactNode } from 'react'

interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'className' | 'id'> {
  label: string
  hint?: ReactNode
  error?: string
  /** A unit or affix rendered inside the field, e.g. `kg`. */
  suffix?: string
}

/**
 * A labelled text field.
 *
 * **The label is always rendered.** A placeholder is not a label: it disappears
 * exactly when the user needs it, and it is invisible to anyone using
 * autofill. Hint and error are wired through `aria-describedby`, so a screen
 * reader reads the field, then the rule, then the failure.
 *
 * Error text is `--over`, never `--accent`: the accent means "look here", and a
 * validation message is not a call to action. `--over` is the same family and
 * carries the meaning "outside the expected range" throughout the app.
 *
 * SUAP-0103.
 */
export function Input({ label, hint, error, suffix, ...rest }: InputProps) {
  const id = useId()
  const hintId = `${id}-hint`
  const errorId = `${id}-error`
  const describedBy = [hint === undefined ? null : hintId, error === undefined ? null : errorId]
    .filter(Boolean)
    .join(' ')

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-sm font-medium text-ink">
        {label}
      </label>

      <div
        className={[
          'flex min-h-11 items-center gap-2 rounded-input border bg-surface px-3',
          'focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-ink',
          error === undefined ? 'border-hairline' : 'border-over',
        ].join(' ')}
      >
        <input
          {...rest}
          id={id}
          aria-invalid={error === undefined ? undefined : true}
          aria-describedby={describedBy === '' ? undefined : describedBy}
          // The wrapper carries the focus ring, so the input itself must not
          // draw a second one inside it.
          //
          // `min-h-11 self-stretch` rather than padding: the *input* is the
          // touch target, and a 40px input inside a 44px wrapper leaves a 2px
          // dead band at the top and bottom that swallows taps. Measured at 40px
          // in the SUAP-0103 verification pass and fixed there.
          className="min-h-11 min-w-0 flex-1 self-stretch bg-transparent text-base text-ink outline-none placeholder:text-ink-soft"
        />
        {suffix === undefined ? null : (
          <span aria-hidden className="shrink-0 text-sm text-ink-soft">
            {suffix}
          </span>
        )}
      </div>

      {hint === undefined ? null : (
        <p id={hintId} className="text-xs text-ink-soft">
          {hint}
        </p>
      )}
      {error === undefined ? null : (
        <p id={errorId} className="text-xs text-over">
          {error}
        </p>
      )}
    </div>
  )
}
